import React from "react";
import { formatBayHireDate } from "@features/bay-hire/utils/formatBayHireDate";
import type { BayHireRequest } from "@services/bayHireApi";
import { useTranslation } from "react-i18next";

interface FieldProps {
  label: string;
  value: React.ReactNode;
  testId?: string;
}

// Megaplan (2026-08-05): label and value used to stack vertically - changed to render inline,
// matching the identical fix already applied to CompanyDetailsCard.tsx's own (separately-
// duplicated) Field component, so the two cards on this same detail page stay visually consistent
// with each other. A single text flow with a real space after the colon (see that component's own
// comment for why the earlier flex+gap attempt still read as cramped).
const Field: React.FC<FieldProps> = ({ label, value, testId }) => (
  <div className="py-3">
    <p className="text-sm text-gray-900 break-words" data-testid={testId}>
      <span className="text-sm font-medium text-gray-500">{label}: </span>
      {value}
    </p>
  </div>
);

const Row: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 border-b border-gray-100 last:border-b-0">
    {children}
  </div>
);

interface BayInformationCardProps {
  request: BayHireRequest;
}

/**
 * DFT-0027: Figma p10 "Bay Information" card - a bordered section with a
 * heading and a two-column label/value grid separated by row dividers.
 *
 * The first four rows are the Figma field set, in Figma order. The rows after
 * them are the pre-approval outcome (amount/chargeable, approved dates,
 * assigned bays), which Figma has no equivalent for but which are real,
 * status-conditional data the Vendor needs - they are folded in as extra rows
 * of the same grid rather than dropped.
 */
/**
 * DFT-0044 (D46/AC44): `Size (m²)` is read from the bay's criteria snapshot - `P_SIZE` was already
 * mapped by DFT-0011 and merely unrendered.
 *
 * 🔴 Written against the LIVE data, after a first version that would have shown "-" for every bay.
 * Two things were wrong in it, and neither was visible from the schema:
 *
 *   1. The criterion code is `size`, lower-case - not `P_SIZE`. `P_SIZE` is the DATABASE column;
 *      Reference Data's mapper emits it as `size`. Matched case-insensitively now.
 *   2. The values are not numbers. Across the 108 bays the combobox returns there are eight-plus
 *      unit spellings - `54Sq.Mtr`, `54Sq.Mtr.`, `45Sq. Mt.`, `54Sq Mtr`, `54SQMTR`, `54 Sq Mtr.`,
 *      `54 SQM`, and bare `54`. The column header already says (m²), and the wireframe shows a plain
 *      number, so the leading number is what is rendered.
 *
 * The `*<n>` multiplier is shown VERBATIM rather than reduced to its first number. 81 of the 215
 * rows carry one (e.g. `80*2`); none appear in the 108 the Bay Hire combobox returns today only
 * because it filters on `bayTypeCode=CBR`, so this is a live shape rather than a hypothetical. A bay
 * recorded as `80*2` is not 80 m², and quietly printing 80 would be a wrong number presented as a
 * right one.
 */
const SIZE_CRITERION_CODE = "size";

export const formatBaySize = (criteriaSnapshot?: Record<string, unknown> | null): string => {
  if (!criteriaSnapshot) return "-";

  const entry = Object.entries(criteriaSnapshot).find(
    ([code]) => code.toLowerCase() === SIZE_CRITERION_CODE,
  );
  const raw = entry?.[1];
  if (raw === undefined || raw === null || String(raw).trim() === "") return "-";

  const value = String(raw).trim();
  // A multiplier is not a single measurement - show what the catalogue actually says.
  if (value.includes("*")) return value;

  const leadingNumber = /^(\d+(?:\.\d+)?)/.exec(value);
  return leadingNumber ? leadingNumber[1] : value;
};

const BayInformationCard: React.FC<BayInformationCardProps> = ({ request }) => {
  const { t } = useTranslation();

  return (
    <section
      className="border border-gray-200 rounded-md bg-white p-6"
      data-testid="bay-hire-detail-bay-information"
    >
      <h3 className="text-base font-semibold text-gray-900 mb-2">
        {t("bay-hire.detail.bay-information")}
      </h3>

      <Row>
        <Field
          // Megaplan (2026-08-05): "Bay No" mislabeled the bay COUNT here, distinct from the
          // Bays table's own "Bay No" column below (the bay CODE, bay-hire.detail.bayNoColumn) -
          // renamed to "No of bays" to remove the ambiguity between the two.
          label={t("bay-hire.detail.bayNo")}
          value={request.numberOfBays ?? "-"}
        />
        <Field
          label={t("bay-hire.detail.requestBay")}
          // FE-43: humanised, not the raw enum. Same pattern already used for the status chip
          // (t(`bay-hire.status.${status}`, { defaultValue: status })) - namespaced by the raw
          // value, falling back to it if a translation is ever missing, so this can never render
          // blank for a value the labels haven't caught up with yet.
          value={t(`bay-hire.bayPreference.${request.bayPreference}`, {
            defaultValue: request.bayPreference,
          })}
        />
      </Row>

      {/* DFT-0044 FE-41: `Name of Port company responsible` / `Designation in the company` moved
          out of this card and into `CompanyDetailsCard`, where the baseline groups them. The keys
          (`detail.responsibleCompanyPerson`, `detail.responsibleDesignation`) are unchanged and are
          now consumed there. */}

      <Row>
        <Field label={t("bay-hire.detail.dateFrom")} value={formatBayHireDate(request.dateFrom)} />
        <Field label={t("bay-hire.detail.dateTo")} value={formatBayHireDate(request.dateTo)} />
      </Row>

      <Row>
        <Field
          label={t("bay-hire.detail.payment")}
          value={t(`bay-hire.paymentType.${request.paymentType}`, {
            defaultValue: request.paymentType,
          })}
        />
        <Field
          label={t("bay-hire.detail.remark")}
          value={request.remarks || t("bay-hire.detail.noRemarks")}
        />
      </Row>

      {(request.totalAmount != null || (request.dateFromApproved && request.dateToApproved)) && (
        <Row>
          {request.totalAmount != null ? (
            <Field
              label={t("bay-hire.detail.preApprovedAmount")}
              testId="bay-hire-detail-total-amount"
              value={
                <>
                  {request.totalAmount} (
                  {request.chargeable
                    ? t("bay-hire.detail.chargeable")
                    : t("bay-hire.detail.notChargeable")}
                  )
                </>
              }
            />
          ) : (
            // FE-40b/FE-77 (2026-08-03): a record with no recorded amount (e.g. an Approved
            // request whose amount was never captured) still renders the label with a dash,
            // matching the wireframe's -8/-9 screens and every other missing-value field in
            // this card - it was previously omitted entirely, silently reproducing the gap
            // rather than showing it.
            <Field
              label={t("bay-hire.detail.preApprovedAmount")}
              testId="bay-hire-detail-total-amount"
              value="-"
            />
          )}
          {request.dateFromApproved && request.dateToApproved ? (
            <Field
              label={t("bay-hire.detail.approvedDateRange")}
              value={`${formatBayHireDate(request.dateFromApproved)} - ${formatBayHireDate(
                request.dateToApproved,
              )}`}
            />
          ) : (
            <div />
          )}
        </Row>
      )}

      {request.bayAssignments && request.bayAssignments.length > 0 && (
        <Row>
          <div className="py-3 md:col-span-2">
            {/* DFT-0044 (05_Port_License_details-1): a `Bays` TABLE with Bay No and Size (m²), not the
                bullet list this replaced. Renamed from "Assigned Bays" because Operations no longer
                assigns them - the Shipping Agent selects them at request time (D35/D43), so from
                Operations' point of view these are the bays that were REQUESTED. */}
            <h4 className="text-sm font-medium text-gray-500 mb-2">{t("bay-hire.detail.bays")}</h4>
            {/* DFT-0044 column mapping, per explicit correction: DB {id, bayNo, size} -> UI
                {Bay No, Description, Size}. `id` (the reference-data catalogue code, e.g. "CB023")
                is what the UI's `Bay No` column shows; `bayNo` (the catalogue's human label, e.g.
                "BAY No.023") is what the UI's `Description` column shows. This REPLACES an earlier
                Description that read the `bayType` criterion ("cargo bay rental") - that mapping is
                gone, not merely superseded, per this correction.
                referenceDataBayId is the FK to that same catalogue id, so it is always present here
                (no fallback needed, unlike bayNoSnapshot below it). */}
            <table
              className="text-sm text-gray-900 w-full max-w-md"
              data-testid="bay-hire-detail-bay-assignments"
            >
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-3 py-2 text-sm font-semibold">
                    {t("bay-hire.detail.bayNoColumn")}
                  </th>
                  <th className="px-3 py-2 text-sm font-semibold">
                    {t("bay-hire.detail.bayDescription")}
                  </th>
                  <th className="px-3 py-2 text-sm font-semibold">
                    {t("bay-hire.detail.baySize")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {request.bayAssignments.map((assignment) => (
                  <tr key={assignment.id} className="border-b border-gray-100">
                    <td className="px-3 py-2">{assignment.referenceDataBayId}</td>
                    <td className="px-3 py-2">{assignment.bayNoSnapshot ?? "-"}</td>
                    <td className="px-3 py-2">{formatBaySize(assignment.criteriaSnapshot)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Row>
      )}
    </section>
  );
};

export default BayInformationCard;
